import { RenderTexture, Text, useCursor, useTexture, type TextProps } from '@react-three/drei'
import { useState } from 'react'
import { MathUtils, type Vector3Tuple } from 'three'

import { RenderTextureScene } from '@components/helpers'
import { WoodenSignDirectionsModel } from '@components/models'
import { useIsTouch } from '@hooks'
import type { ThreeElements } from '@react-three/fiber'
import { GamePhase, useGameStore } from '@stores'
import { useCreditsControls } from '@utils'
import { WoodenSignBillboard } from './WoodenSignBillboard'
import { WoodenSignButton } from './WoodenSignButton'

type E = { stopPropagation(): void }

function armHoverProps(index: number, setter: (i: number) => void) {
  return {
    onPointerOver: (e: E) => {
      e.stopPropagation()
      setter(index)
    },
    onPointerOut: (e: E) => {
      e.stopPropagation()
      setter(-1)
    },
    onPointerEnter: (e: E) => {
      e.stopPropagation()
      setter(index)
    },
    onPointerLeave: (e: E) => {
      e.stopPropagation()
      setter(-1)
    },
  }
}

function Content({ children, ...props }: TextProps) {
  return (
    <Text font="/fonts/jaro.ttf" anchorX="center" anchorY="middle" textAlign="center" {...props}>
      {children}
    </Text>
  )
}

function SignPlane({ children, ...props }: ThreeElements['mesh']) {
  return (
    <mesh position={[0, 1.65, 0.03]} scale={[3.5, 5, 1]} renderOrder={5} {...props}>
      <planeGeometry />
      <meshBasicMaterial transparent depthTest={false}>
        <RenderTexture attach="map" width={512} height={512}>
          <RenderTextureScene>{children}</RenderTextureScene>
        </RenderTexture>
      </meshBasicMaterial>
    </mesh>
  )
}

export interface CreditLinkMeta {
  id: string
  label: string
  url: string
  defaultPosition: Vector3Tuple
  defaultRotationZ: number
}

const CREDIT_LINKS_META: CreditLinkMeta[] = [
  {
    id: 'kenney',
    label: 'Train Kit by Kenney',
    url: 'https://kenney.nl/',
    defaultPosition: [-1.4, 4.7, 0] as Vector3Tuple,
    defaultRotationZ: 0.2,
  },
  {
    id: 'ipoly',
    label: 'Wooden Signs by iPoly3D',
    url: 'https://poly.pizza/u/iPoly3D',
    defaultPosition: [0.3, 2.8, 0] as Vector3Tuple,
    defaultRotationZ: 4.1,
  },
  {
    id: 'jair',
    label: 'Railroad crossing by Jair Trejo',
    url: 'https://poly.pizza/u/Jair%20Trejo',
    defaultPosition: [0, 0.8, 0] as Vector3Tuple,
    defaultRotationZ: 0.1,
  },
  {
    id: 'quaternius',
    label: 'Flowers, rocks and trees by Quaternius',
    url: 'https://quaternius.com/',
    defaultPosition: [1.5, -1.2, 0] as Vector3Tuple,
    defaultRotationZ: -0.2,
  },
  {
    id: 'pixabay',
    label: 'SFX from Pixabay',
    url: 'https://pixabay.com/it/playlists/a-little-loco-32531006',
    defaultPosition: [0.2, -3.4, 0] as Vector3Tuple,
    defaultRotationZ: -6.6,
  },
]

export function Credits() {
  const {
    debug,
    groupPosition,
    footerPosition,
    bmcPosition,
    backPosition,
    backRotation,
    creditsPosition,
    creditsRotation,
    ...links
  } = useCreditsControls(CREDIT_LINKS_META)

  const creditLinks = CREDIT_LINKS_META.map(({ id, label, url }) => ({
    label,
    url,
    position: links[`${id}_position`] as Vector3Tuple,
    rotationZ: links[`${id}_rotationZ`] as number,
  }))

  const isTouch = useIsTouch()
  const [hoveredCreditIndex, setHoveredCreditIndex] = useState(-1)
  useCursor(hoveredCreditIndex !== -1)

  const phase = useGameStore(state => state.phase)
  const setPhase = useGameStore(state => state.setPhase)

  const isTitle = phase === GamePhase.TITLE

  const bmcTexture = useTexture('/bmc.png')

  return (
    <>
      <WoodenSignBillboard
        label="Made with ♡ by dammafra"
        labelProps={{ scale: 1.4 }}
        disabled={!isTitle}
        onClick={() => window.open('https://dammafra.dev', '_blank')}
        position={footerPosition}
        scale={0.8}
        rotation-y={MathUtils.degToRad(-35)}
      />

      <WoodenSignBillboard
        disabled={!isTitle}
        onClick={() => window.open('https://www.buymeacoffee.com/dammafra', '_blank')}
        position={bmcPosition}
        scale={0.5}
        rotation-y={MathUtils.degToRad(-45)}
      >
        <mesh scale={2.25}>
          <planeGeometry args={[7, 2]} />
          <meshBasicMaterial map={bmcTexture} transparent />
        </mesh>
      </WoodenSignBillboard>

      <group position={groupPosition}>
        <mesh visible={debug} position-y={1.5} scale={4} name="credits">
          <boxGeometry />
          <meshBasicMaterial color="red" wireframe />
        </mesh>

        <group position={creditsPosition} rotation-y={MathUtils.degToRad(creditsRotation)}>
          <WoodenSignDirectionsModel
            scale={1.5}
            armsProps={creditLinks.map(({ url }, index) => ({
              highlight: isTouch || hoveredCreditIndex === index,
              ...armHoverProps(index, setHoveredCreditIndex),
              onClick: (e: E) => {
                e.stopPropagation()
                window.open(url, '_blank')
              },
            }))}
          />
          <SignPlane position-z={0.07}>
            {creditLinks.map(({ label, url, position, rotationZ }) => (
              <Content
                key={url}
                position={position}
                rotation-z={MathUtils.degToRad(rotationZ)}
                scale={0.8}
              >
                {label}
              </Content>
            ))}
          </SignPlane>
        </group>

        <WoodenSignButton
          label="Back"
          onClick={() => setPhase(GamePhase.TITLE)}
          position={backPosition}
          rotation-y={MathUtils.degToRad(backRotation)}
        />
      </group>
    </>
  )
}
